/**
 * Ramo "pequeno-almoço / brunch" (ADITIVO).
 *
 * Deteta a intenção própria de pequeno-almoço ou brunch, a janela horária
 * aplicável e — sobretudo — se o pedido é de DESCOBERTA (zonas e locais
 * recomendados, sem crédito) ou de ROTA (itinerário com bloco de refeição).
 *
 * Regra vinculativa de precedência:
 *  1. sem pedido explícito de rota e sem duração → Descoberta, mesmo com "só";
 *  2. com rota explícita ou duração → rota;
 *  3. o foco exclusivo controla apenas QUE locais entram, nunca o modo.
 */

export type BreakfastKind = "breakfast" | "brunch";

/** Janela horária em "HH:MM". */
export type BreakfastWindow = { start: string; end: string };

export type BreakfastIntent = {
  kind: BreakfastKind;
  window: BreakfastWindow;
  /** A hora veio escrita no pedido (tem sempre prioridade). */
  explicitTime: boolean;
  /** "só pequeno-almoço" — filtro exclusivo dos locais permitidos. */
  exclusive: boolean;
  /** Modo obrigatório resultante do pedido. */
  mode: "discovery" | "route";
};

export const DEFAULT_BREAKFAST_WINDOW: BreakfastWindow = { start: "08:30", end: "09:30" };
export const DEFAULT_BRUNCH_WINDOW: BreakfastWindow = { start: "10:30", end: "12:00" };

const W = "(?<![\\p{L}])";
const Wend = "(?![\\p{L}])";

const BREAKFAST_RE = new RegExp(
  `${W}(pequenos?[\\s-]*almo[cç]os?|pequeno[\\s-]*almo[cç]o|desjejum|breakfasts?|desayunos?|petits?[\\s-]*d[ée]jeuners?|colazione|fr[üu]hst[üu]ck)${Wend}`,
  "iu",
);

const BRUNCH_RE = new RegExp(`${W}(brunch(?:es)?)${Wend}`, "iu");

const ROUTE_RE = new RegExp(
  `${W}(rota|rotas|percurso\\w*|itiner[aá]rio\\w*|roteiro\\w*|routes?|circuito|tour)${Wend}`,
  "iu",
);

const DURATION_RE = new RegExp(
  `(\\d+\\s*(h${Wend}|hrs?${Wend}|horas?${Wend}|hours?${Wend}|dias?${Wend}|days?${Wend}|giorni?${Wend}|jours?${Wend}|tage?${Wend}))|(${W}(uma|um|meio|meia)\\s+(dia|semana|tarde|manh[ãa])${Wend})`,
  "iu",
);

const EXCLUSIVE_RE = new RegExp(
  `${W}(s[oó]|apenas|somente|unicamente|only|just|nur|solamente|seulement|uniquement)${Wend}`,
  "iu",
);

/** "às 8:30", "at 9am", "pelas 10h", "vers 11h30". */
const TIME_RE = /(?:\b(?:às|as|at|pelas|por\s+volta\s+das|hacia\s+las|a\s+las|vers|alle|um|gegen)\s*)?\b(\d{1,2})(?:[:h.](\d{2}))?\s*(am|pm)?\b/iu;

const pad = (n: number) => String(n).padStart(2, "0");
const toHhmm = (min: number) => `${pad(Math.floor(min / 60) % 24)}:${pad(min % 60)}`;

function explicitWindow(text: string): BreakfastWindow | null {
  // Só consideramos horas escritas junto de um marcador horário explícito.
  const anchored =
    /(?:às|\bas\b|\bat\b|\bpelas\b|\bpor\s+volta\s+das\b|\bhacia\s+las\b|\ba\s+las\b|\bvers\b|\balle\b|\bum\b|\bgegen\b)\s*\d{1,2}(?:[:h.]\d{2})?\s*(?:am|pm|h)?/iu.exec(
      text,
    );
  if (!anchored) return null;
  const m = TIME_RE.exec(anchored[0]);
  if (!m) return null;
  let hour = Number.parseInt(m[1], 10);
  const minute = m[2] ? Number.parseInt(m[2], 10) : 0;
  const suffix = (m[3] ?? "").toLowerCase();
  if (!Number.isFinite(hour) || hour > 23 || minute > 59) return null;
  if (suffix === "pm" && hour < 12) hour += 12;
  if (suffix === "am" && hour === 12) hour = 0;
  const start = hour * 60 + minute;
  return { start: toHhmm(start), end: toHhmm(Math.min(23 * 60 + 59, start + 60)) };
}

export function detectBreakfastIntent(text: string): BreakfastIntent | null {
  const raw = (text ?? "").trim();
  if (!raw) return null;
  const hasBreakfast = BREAKFAST_RE.test(raw);
  const hasBrunch = BRUNCH_RE.test(raw);
  if (!hasBreakfast && !hasBrunch) return null;

  const kind: BreakfastKind = hasBreakfast ? "breakfast" : "brunch";
  const explicit = explicitWindow(raw);
  const window =
    explicit ?? (kind === "brunch" ? DEFAULT_BRUNCH_WINDOW : DEFAULT_BREAKFAST_WINDOW);

  const wantsRoute = ROUTE_RE.test(raw) || DURATION_RE.test(raw);

  return {
    kind,
    window,
    explicitTime: Boolean(explicit),
    exclusive: EXCLUSIVE_RE.test(raw),
    mode: wantsRoute ? "route" : "discovery",
  };
}

/**
 * Foco exclusivo `breakfast` de rota: só quando a pessoa pede expressamente
 * uma rota (ou duração) e restringe o pedido ao pequeno-almoço/brunch.
 * Num pedido normal com duração, `breakfast` fica limitado ao bloco.
 */
export function breakfastRouteFocus(text: string): "breakfast" | null {
  const intent = detectBreakfastIntent(text);
  if (!intent) return null;
  return intent.mode === "route" && intent.exclusive ? "breakfast" : null;
}
